import { Box, Button, Card, CardContent, Stack, Tab, Tabs, Typography } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import FuturesAllocationListView from './futures/FuturesAllocationListView'
import FuturesBankView from './futures/FuturesBankView'
import FuturesGenericListView from './futures/FuturesGenericListView'
import FuturesLocalPriceDialog from './futures/FuturesLocalPriceDialog'
import FuturesLocalPriceView from './futures/FuturesLocalPriceView'
import FuturesOrdersListView from './futures/FuturesOrdersListView'
import FuturesReportView from './futures/FuturesReportView'
import SpreadsOrdersListView from './futures/SpreadsOrdersListView'
import PageHeader from '../components/PageHeader'

type FuturesTabKey =
  | 'orders'
  | 'spreads'
  | 'allocation'
  | 'adjust-lot'
  | 'bank-cash-in'
  | 'bank-statement'
  | 'bank-cost-change'
  | 'market'
  | 'local-price'
  | 'margin-report'
  | 'summary'
  | 'cash-balance'
  | 'position'
  | 'pnl'
  | 'check-futures'
  | 'reconciliation'

interface FuturesTabConfig {
  key: FuturesTabKey
  label: string
  accessLabel: string
}

const TAB_CONFIG: FuturesTabConfig[] = [
  { key: 'orders', label: 'Futures Orders', accessLabel: 'Futures' },
  { key: 'spreads', label: 'Spreads Orders', accessLabel: 'Spreads' },
  { key: 'allocation', label: 'Allocation', accessLabel: 'Allocation' },
  { key: 'adjust-lot', label: 'Adjust Lot', accessLabel: 'Adjustment' },
  { key: 'bank-cash-in', label: 'Bank Cash In', accessLabel: 'Cash-in' },
  { key: 'bank-statement', label: 'Bank Statement', accessLabel: 'Bank Statement' },
  { key: 'bank-cost-change', label: 'Bank Cost Change', accessLabel: 'Bank cost change' },
  { key: 'market', label: 'Market', accessLabel: 'Market' },
  { key: 'local-price', label: 'Local Price', accessLabel: 'Input Local Price' },
  { key: 'margin-report', label: 'Margin Report', accessLabel: 'Margin Report' },
  { key: 'summary', label: 'Summary', accessLabel: 'SUMMARY' },
  { key: 'cash-balance', label: 'Cash Balance', accessLabel: 'Cash Balance' },
  { key: 'position', label: 'Position', accessLabel: 'Position' },
  { key: 'pnl', label: 'PnL', accessLabel: 'PnL' },
  { key: 'check-futures', label: 'Check Futures', accessLabel: 'Check Futures' },
  { key: 'reconciliation', label: 'Reconciliation', accessLabel: 'Reconciliation' },
]

const headerButtonSx = {
  minHeight: 24,
  px: 0.75,
  py: 0.15,
  fontSize: '0.67rem',
  lineHeight: 1.1,
  textTransform: 'none' as const,
}

function isFuturesTabKey(value: string | undefined): value is FuturesTabKey {
  return TAB_CONFIG.some((tab) => tab.key === value)
}

function renderTab(key: FuturesTabKey, openLocalPrice: () => void) {
  switch (key) {
    case 'orders':
      return <FuturesOrdersListView />
    case 'spreads':
      return <SpreadsOrdersListView />
    case 'allocation':
      return <FuturesAllocationListView />
    case 'adjust-lot':
      return <FuturesGenericListView kind="adjust-lot" />
    case 'bank-cash-in':
      return <FuturesBankView mode="cash-in" />
    case 'bank-statement':
      return <FuturesBankView mode="bank-statement" />
    case 'bank-cost-change':
      return <FuturesGenericListView kind="bank-cost-change" />
    case 'market':
      return <FuturesGenericListView kind="market" />
    case 'local-price':
      return <FuturesLocalPriceView onOpenDialog={openLocalPrice} />
    case 'margin-report':
      return <FuturesReportView kind="margin-report" />
    case 'summary':
      return <FuturesReportView kind="summary" />
    case 'cash-balance':
      return <FuturesReportView kind="cash-balance" />
    case 'position':
      return <FuturesReportView kind="position" />
    case 'pnl':
      return <FuturesReportView kind="pnl" />
    case 'check-futures':
      return <FuturesReportView kind="check-futures" />
    case 'reconciliation':
      return <FuturesReportView kind="reconciliation" />
    default:
      return <FuturesReportView kind="summary" />
  }
}

export default function FuturesControlPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const [localPriceOpen, setLocalPriceOpen] = useState(false)

  const activeKey: FuturesTabKey = useMemo(() => {
    const segments = location.pathname.split('/').filter(Boolean)
    const candidate = segments[1]
    return isFuturesTabKey(candidate) ? candidate : 'summary'
  }, [location.pathname])

  useEffect(() => {
    if (!location.pathname.startsWith('/futures')) return

    const segments = location.pathname.split('/').filter(Boolean)
    const candidate = segments[1]
    if (!candidate || !isFuturesTabKey(candidate)) {
      navigate('/futures/summary', { replace: true })
    }
  }, [location.pathname, navigate])

  const activeTab = TAB_CONFIG.find((tab) => tab.key === activeKey) ?? TAB_CONFIG.find((tab) => tab.key === 'summary')

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <PageHeader
        title="Futures Control"
        module="Futures"
        actions={
          <Stack
            direction="row"
            spacing={0.6}
            alignItems="center"
            useFlexGap
            flexWrap="wrap"
            justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}
            sx={{ width: '100%' }}
          >
            <Button size="small" variant="outlined" sx={headerButtonSx} onClick={() => setLocalPriceOpen(true)}>
              Input Local Price
            </Button>
            <Button size="small" variant="outlined" sx={headerButtonSx}>
              Export Excel
            </Button>
          </Stack>
        }
      />

      <Card sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Tabs
          value={activeKey}
          onChange={(_, value: FuturesTabKey) => {
            navigate(`/futures/${value}`, { replace: false })
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            minHeight: 38,
            '& .MuiTabs-indicator': { height: 2 },
            '& .MuiTab-root': {
              minHeight: 38,
              py: 0.25,
              px: 1.5,
              fontSize: '0.8rem',
              fontWeight: 600,
              textTransform: 'none',
            },
          }}
        >
          {TAB_CONFIG.map((tab) => (
            <Tab key={tab.key} label={tab.label} value={tab.key} />
          ))}
        </Tabs>

        <CardContent sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', pt: 2 }}>
          <Stack spacing={1.25} sx={{ flex: 1, minHeight: 0 }}>
            {activeTab && (
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>
                  {activeTab.label}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Access label: {activeTab.accessLabel}
                </Typography>
              </Box>
            )}
            {renderTab(activeKey, () => setLocalPriceOpen(true))}
          </Stack>
        </CardContent>
      </Card>

      <FuturesLocalPriceDialog open={localPriceOpen} onClose={() => setLocalPriceOpen(false)} />
    </Box>
  )
}
